import { Box, Container, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type {
  OrganisationEditFormOrganisation,
  OrganisationEditFormValues
} from '@/features/users/components/OrganisationEditForm';
import {
  getGetOrganisationQueryKey,
  useGetOrganisation,
  useUpdateProfile,
  useUploadLogo
} from '@/api/generated/organisations/organisations';
import { useListCountries, useListCurrencies } from '@/api/generated/reference-data/reference-data';
import { InfoBanner, PageHeading } from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { OrganisationEditForm } from '@/features/users/components/OrganisationEditForm';
import { buildCountryOptions } from '@/utils/format';
import { useAuthenticatedImage } from '@/utils/useAuthenticatedImage';

export const OrganisationProfilePage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const orgId = user?.organisationId ?? '';

  const { data } = useGetOrganisation(orgId, { query: { enabled: !!orgId } });
  const profile = data as unknown as OrganisationEditFormOrganisation | undefined;

  const { data: countries } = useListCountries();
  const { data: currencies } = useListCurrencies();
  const countryOptions = buildCountryOptions(countries);
  const currencyOptions =
    currencies?.map((c) => ({ value: c.code ?? '', label: `${c.code ?? ''} — ${c.name ?? ''}` })) ??
    [];

  const updateMutation = useUpdateProfile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganisationQueryKey(orgId) });
      }
    }
  });

  const uploadLogoMutation = useUploadLogo({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganisationQueryKey(orgId) });
      }
    }
  });

  const logoSrc = useAuthenticatedImage(
    profile?.logoKey
      ? `/api/v1/organisations/${orgId}/logo?v=${encodeURIComponent(profile.logoKey)}`
      : null
  );

  const handleSubmit = (values: OrganisationEditFormValues) => {
    updateMutation.mutate({
      id: orgId,
      data: {
        name: values.name,
        address: values.address || undefined,
        currency: values.currency,
        phone: values.phone || undefined,
        email: values.email || undefined,
        city: values.city || undefined,
        postalCode: values.postalCode || undefined,
        contactPerson: values.contactPerson || undefined,
        website: values.website || undefined,
        offerTermsText: values.offerTermsText || undefined,
        iban: values.iban || undefined,
        bankName: values.bankName || undefined,
        bic: values.bic || undefined
      }
    });
  };

  const handleLogoSelected = (file: File) => {
    uploadLogoMutation.mutate({ id: orgId, data: { file } });
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={20}>
        <PageHeading title={t('orgProfile.title')} subtitle={t('orgProfile.subtitle')} />

        <Box maw={640} style={{ width: '100%' }}>
          <InfoBanner message={t('orgProfile.auditBanner')} />
        </Box>

        {profile && (
          <Box maw={640} style={{ width: '100%' }}>
            <Stack gap={12}>
              <OrganisationEditForm
                organisation={profile}
                onSubmit={handleSubmit}
                showCancel={false}
                loading={updateMutation.isPending}
                isPaused={updateMutation.isPaused}
                countryOptions={countryOptions}
                currencyOptions={currencyOptions}
                logoSrc={logoSrc}
                onLogoSelected={handleLogoSelected}
                logoUploading={uploadLogoMutation.isPending}
              />
              <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
                {t('orgProfile.auditFooterNote')}
              </Text>
            </Stack>
          </Box>
        )}
      </Stack>
    </Container>
  );
};
